import React, { useState } from 'react';

import {
  Icon,
  Table,
  NativeFilterTypes,
  Button,
  PopupButton,
} from 'design-system-web';
import { M } from 'components/typography';
import { useIsAdminView } from 'hooks';
import { useTranslation, Trans } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { useQuery } from '@apollo/client';
import { routes } from 'lane-shared/config';
import { Chip } from 'components/ads';
import {
  ActivateVisitor,
  EventStatus,
} from 'lane-shared/domains/visitorManagement/types';
import type { VisitorPassQueryResponse } from 'application-types';
import { getScheduledVisits as getScheduledVisitsGraphqlQueries } from 'graphql-queries';
import { getScheduledVisits as getScheduledVisitsLaneShared } from 'lane-shared/graphql/visitorManagement';
import { hasPermission } from 'lane-shared/helpers';
import { getTimeZoneByGeoLocation } from 'async-timing';
import {
  SHORT_TIME_WITH_TZ,
  SIMPLE_DATE,
} from 'lane-shared/helpers/constants/dates';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { convertTo62, convertToUUID, convertIdToUUID } from 'uuid-encoding';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { useDymoPrinter, useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import {
  VisitorPassStatusSelect,
  VisitorLogTableFilters,
} from '../../../../components';
import { getAllowedStatuses, INVALID_TRANSITION_STATUSES } from '../../../../components/VisitorPassStatusSelect/statusFilterHelper';
import { useAfterRefresh } from '../../../../hooks/useAfterRefresh';
import { useStatusSelector } from '../../../../hooks/useStatusSelector';
import {
  StatusListItems,
  StatusKeyToLabelForExport,
  StatusKeyToLabelForStatusUpdate,
} from '../../../../types/EventStatus';
import type { StatusListItem } from '../../../../types/EventStatus';
import history from 'helpers/history';

import styles from './styles.scss';
import { ColumnFiltersState } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { exportCSV } from '../../../../helpers/exportCSV';
import { Tooltip } from 'components';
import useThemeColors from 'lane-shared/hooks/useThemeColors';
import { PERMISSION_KEYS } from 'constants-permissions';
import { UserType } from 'lane-shared/types/User';
import { useOrganizationSettingsData } from 'lane-shared/domains/visitorManagement/hooks/useOrganizationSettingsData';
import { PrinterSelectionModal } from '../../modals/printer-selection/PrinterSelectionModal';
import { getScheduledVisits } from './helpers/getScheduledVisits';
import { useDownloadQRCode } from 'hooks/visitorManagement/useDownloadQRCode';
import { InternalError } from 'activate-errors';
import { Channel } from 'lane-shared/types/ChannelType';
import { Alert, AlertType } from 'components/lds';
import { WatchlistSeverityLevel } from 'lane-shared/domains/visitorManagement/types/VisitorPass';
import { VisitorPassUpdateResult } from 'graphql-query-contracts';
import {
  getWatchlistSeverityTranslation,
  getWatchlistSeverityChipStyle,
  isValidWatchlistSeverityLevel,
  getHighestWatchlistSeverityLevel,
} from '../../../../helpers/watchlistSeverityHelpers';
import { getVisitorLogInviteNameColumns } from '../../getVisitorLogInviteNameColumns';
import { useVisitorCustomFieldColumns } from 'domains/visitorManagement/hooks/useVisitorCustomFieldColumns';

const TRANSLATION_KEYS = {
  recurrenceTooltip:
    'web.admin.channel.visitor.log.scheduled.recurrence.tooltip',
  watchlistSeverityTooltip:
    'web.admin.channel.visitor.log.scheduled.watchlist.severity.tooltip',
};

export type ScheduledVisitsResponse = {
  scheduledVisits: {
    result: {
      visits: VisitorPassQueryResponse[];
      eventNames: string[];
      tenantNames: string[];
    };
    totalCount: number;
  };
};

type ColumnVisibilityState = Record<string, boolean>;

export type VisitorRow = Pick<
  ActivateVisitor,
  'firstName' | 'lastName' | 'company' | 'email'
>;

export type VisitorPassRow = Pick<
  VisitorPassQueryResponse,
  | 'companyName'
  | 'createdAtDatetime'
  | 'endDatetime'
  | 'eventName'
  | 'hostName'
  | 'inviteName'
  | 'tenantName'
  | 'floor'
  | 'id'
  | 'staffNote'
  | 'sourceId'
  | 'startDatetime'
  | 'status'
  | 'submissionId'
  | 'submittedBy'
> & { visitor: VisitorRow };

const unavailableFilters = [
  EventStatus.EVENT_STATUS_EXPIRED.toString(),
];

const unavailableForBulkActionStatuses = [
  EventStatus.EVENT_STATUS_UPCOMING.toString(),
  EventStatus.EVENT_STATUS_CANCELLED.toString(),
  EventStatus.EVENT_STATUS_WITHDRAWN.toString(),
  EventStatus.EVENT_STATUS_PENDING.toString(),
  EventStatus.EVENT_STATUS_EXPIRED.toString(),
  EventStatus.EVENT_STATUS_REJECTED.toString(),
  EventStatus.EVENT_STATUS_FAILED.toString(),
];

let searchKeyValue: string = '';
export function VisitorScheduledComponent({
  user,
  channel,
}: {
  user: UserType;
  channel: Channel;
}) {
  const { t } = useTranslation();
  const [, channelSlug] = useIsAdminView();
  const { handleRefresh } = useAfterRefresh();

  const bulkActionsEnabled = useFlag(FeatureFlag.EnableVMBulkActions, false);
  const displayAllBulkActionStatuses = useFlag(
    FeatureFlag.VisitorManagementDisplayAllBulkActionStatuses,
    false
  );
  const isWatchlistV2Enabled = useFlag(
    FeatureFlag.VisitorManagementWatchlistV2,
    false
  );
  const isIGSRefactorEnabled = useFlag(
    FeatureFlag.VisitorManagementIGSRefactor,
    false
  );
  const showVisitorExtraInfo = useFlag(
    FeatureFlag.VisitorManagementVisitorLogInfo,
    false
  );

  const showVisitorInviteName = useFlag(
    FeatureFlag.VisitorInviteName,
    false
  );

  const isStatusVisible = (status: string): boolean => {
    if (status === EventStatus.EVENT_STATUS_REJECTED) {
      return !!isWatchlistV2Enabled;
    }
    if (status === EventStatus.EVENT_STATUS_PENDING) {
      return !!(isIGSRefactorEnabled || isWatchlistV2Enabled);
    }
    if (status === EventStatus.EVENT_STATUS_FAILED) {
      return !!isIGSRefactorEnabled;
    }
    return true;
  };

  const statusListItems = StatusListItems.filter(
    (statusListItem: StatusListItem) =>
      !unavailableFilters.includes(statusListItem.value) &&
      isStatusVisible(statusListItem.value)
  );

  const filterVisitorPassesByWatchlistV2 = (
    visits: VisitorPassQueryResponse[]
  ): VisitorPassQueryResponse[] => visits.filter(visit => isStatusVisible(visit.status));

  const timeZone = getTimeZoneByGeoLocation({
    latitude: channel?.address?.geo[1],
    longitude: channel?.address?.geo[0],
  });
  const currentTime = DateTime.fromJSDate(new Date(), {
    zone: timeZone,
  }).startOf('day');
  const [startDate, setStartDate] = useState<Date>(currentTime.toJSDate());
  const [isSelectPrintersModalOpen, setIsSelectPrintersModalOpen] =
    useState(false);
  const [selectedVisitorPass, setSelectedVisitorPass] =
    useState<VisitorPassQueryResponse | null>(null);
  const [selectedVisitorPasses, setSelectedVisitorPasses] = useState<
    VisitorPassQueryResponse[]
  >([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [tableKey, setTableKey] = useState(0);

  const { downloadQRCode } = useDownloadQRCode();

  const oneYearFromNow = currentTime
    .plus({
      year: 1,
    })
    .startOf('day')
    .toJSDate();
  const endOfDaySelectedFilterDate = DateTime.fromJSDate(startDate, {
    zone: timeZone,
  })
    .endOf('day')
    .toJSDate()
    .toISOString();

  const [visibleColumns, setVisibleColumns] = useState<ColumnVisibilityState>();
  const { customFieldColumns, effectiveColumnVisibility, customFieldsEnabled } =
    useVisitorCustomFieldColumns({
      channelId: channel._id,
      visibleColumns,
      t,
    });

  const scheduledVisitsQuery = showVisitorInviteName || customFieldsEnabled
    ? getScheduledVisitsGraphqlQueries
    : getScheduledVisitsLaneShared;

  const { data, loading, refetch } = useQuery<ScheduledVisitsResponse>(
    scheduledVisitsQuery,
    {
      variables: {
        channelId: channel._id,
        startDate: startDate.toISOString(),
        endDate: endOfDaySelectedFilterDate,
      },
      // Fetch from network first time and store in cache
      fetchPolicy: 'network-only',
      /**
       * Fetch from cache first subsequently.
       * Calling refetch uses the initial fetchPolicy: "network-only".
       */
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      onCompleted: data => {
        handleRefresh(data.scheduledVisits.result.visits.map(p => p.id));
      },
    }
  );

  const { organizationSettings } = useOrganizationSettingsData(
    convertToUUID(channel?._id)
  );

  const refresh = useDebouncedCallback(() => refetch(), 500).callback;
  const { updateStatus, updateBulkStatus, loadingStates, updateLoadingState } =
    useStatusSelector();

  const visitorNotesEnabled = useFlag(
    FeatureFlag.VisitorManagementVisitorNotes,
    false
  );

  const hasAnyPermission = (
    user: UserType,
    permissions: string[],
    channelId: string
  ) => {
    return (
      user.isSuperUser ||
      (channelId && hasPermission(user.roles, permissions, channelId))
    );
  };

  const canPrintVisitorPass = hasPermission(
    user.roles,
    [PERMISSION_KEYS.PERMISSION_VISITOR_MANAGEMENT_LOGS_PRINT_VISITOR_PASS],
    channel?._id,
    false
  );

  const canDownloadQRCode = hasAnyPermission(
    user,
    [PERMISSION_KEYS.PERMISSION_VISITOR_MANAGEMENT_LOGS_DOWNLOAD_QR_CODE],
    channel?._id
  );

  const { printPass, connectedPrinters, getPrinters } = useDymoPrinter(
    channel,
    canPrintVisitorPass
  );

  const [activeFilters, setActiveFilters] = useState<ColumnFiltersState>([
    {
      id: 'startDatetime',
      value: startDate,
    },
  ]);

  const handleDateFilterChange = (date: Date) => {
    const newStartDate = DateTime.fromJSDate(date, { zone: timeZone })
      .startOf('day')
      .toJSDate();

    setStartDate(newStartDate);

    if (selectedVisitorPasses.length > 0 || selectedRows.length > 0) {
      handleClearSelection();
    }
  };
  
  const handleUpdateStatus = async (
    status: string,
    pass: VisitorPassQueryResponse
  ): Promise<void> => {
    try {
      await updateStatus(status, pass);
    } catch (err) {
      const error = err as Error;

      window.Toast.show(error.message, 5000);
    }
  };

  const handleBulkStatusUpdate = async (status: string): Promise<void> => {
    if (selectedVisitorPasses.length === 0) return;

    try {
      const passesWithValidTransitionStatus = selectedVisitorPasses.filter(
        pass => {
          // Check if the status is allowed considering visitor validation rules
          const allowedStatuses = getAllowedStatuses(
            pass,
            organizationSettings
          );

          if (allowedStatuses && allowedStatuses.length > 0) {
            return allowedStatuses
              .map(eventStatus => eventStatus.toString())
              .includes(status);
          }

          const validTransitions = pass.validStateTransitions || [];

          return validTransitions.includes(status);
        }
      );

      if (passesWithValidTransitionStatus.length === 0) {
        window.Toast.show(
          t('web.admin.channel.visitor.log.bulkActions.noValidPasses', {
            status,
          }),
          3000
        );

        return;
      }

      const updateOperationResults = await updateBulkStatus(
        channel._id,
        status,
        passesWithValidTransitionStatus
      );

      await refresh();

      passesWithValidTransitionStatus.forEach(pass => {
        updateLoadingState(pass.id, false);
      });

      const successfulUpdates = updateOperationResults.filter(
        (result: VisitorPassUpdateResult) => result.success
      );
      const totalSelected = selectedVisitorPasses.length;

      handleClearSelection();

      const message = t(
        'web.admin.channel.visitor.log.bulkActions.statusUpdate',
        {
          count: successfulUpdates.length,
          totalSelected,
        }
      );

      window.Toast.show(message, 3000);
    } catch (err) {
      window.Toast.show(err.message, 5000);
    }
  };

  const openPrinterSelectionModal = (row: VisitorPassQueryResponse) => {
    setSelectedVisitorPass(row);
    setIsSelectPrintersModalOpen(true);
  };

  const handlePrintPass = (row: VisitorPassQueryResponse) => {
    const printers = getPrinters();

    if (!printers.length) {
      return alert(t('web.admin.channel.visitor.log.printVisitorPass.error'));
    }

    if (printers.length === 1) {
      return printPass(row);
    }

    openPrinterSelectionModal(row);
  };

  const handleDownloadQRCode = async (row: VisitorPassQueryResponse) => {
    window.Alert.loading({
      title: `${t('web.admin.channel.visitor.log.rowActions.downloadQRCode.preparingYourFile')}`,
      disableCloseOnBackgroundClick: true,
    });

    try {
      const visitorPassUUID = convertIdToUUID(row?.id);

      await downloadQRCode(visitorPassUUID);
    } catch (err) {
      if (err instanceof InternalError) {
        window.Toast.show(
          t(
            'web.admin.channel.visitor.log.rowActions.downloadQRCode.internalError'
          ),
          5000
        );
      } else {
        window.Toast.show(
          t('web.admin.channel.visitor.log.rowActions.downloadQRCode.error'),
          5000
        );
      }
    }

    setTimeout(() => window.Alert.hide(), 500);
  };

  const handleMultiselectChange = (selectedRows: string[]) => {
    const tableData = data?.scheduledVisits?.result?.visits || [];
    const visitorPasses = selectedRows.map(
      (index: string) => tableData[parseInt(index, 10)]
    );

    setSelectedVisitorPasses(visitorPasses);
    setSelectedRows(selectedRows);
  };

  const handleClearSelection = () => {
    setSelectedVisitorPasses([]);
    setSelectedRows([]);
    // Force table to re-render and reset selection
    setTableKey(prev => prev + 1);
  };

  const getFullName = (cell: ActivateVisitor) => {
    return `${cell.firstName} ${cell.lastName}`;
  };

  const mandatoryKeys = new Set(['startDate', 'startDatetime', 'endDatetime']);

  const getTenantFloor = (tenantName: string, floor?: string) => {
    return floor ? `${tenantName} - ${floor}` : tenantName;
  };

  const visitorColumns = [
    {
      header: t('web.admin.channel.visitor.log.columns.visitor'),
      key: 'visitor',
      renderCell: (cell: ActivateVisitor) => (
        <span className={styles.visitorName}>{getFullName(cell)}</span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        `${visitorPass.visitor.firstName} ${visitorPass.visitor.lastName}`,
    },
    {
      header: t('web.admin.channel.visitor.log.columns.visitorCompany'),
      key: 'visitor_company',
      renderCell: (_: any, row: VisitorPassQueryResponse) => (
        <span className={styles.visitorName}>{row.visitor.company}</span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.visitor.company,
    },
  ];

  if (showVisitorExtraInfo) {
    visitorColumns.push({
      header: t('web.admin.channel.visitor.log.columns.visitorEmail'),
      key: 'visitor.email',
      renderCell: (_: any, row: VisitorPassQueryResponse) => (
        <span>{row.visitor.email?.toLowerCase()}</span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.visitor.email,
    });

    visitorColumns.push({
      header: t('web.admin.channel.visitor.log.columns.visitorPhoneNumber'),
      key: 'visitor.phone',
      renderCell: (_: any, row: VisitorPassQueryResponse) => (
        <span className={styles.visitorName}>{row.visitor.phone}</span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.visitor.phone,
    });
  }

  let columns = [
    {
      header: t('web.admin.channel.visitor.log.columns.status'),
      key: 'status',
      renderCell: (_: any, row: VisitorPassQueryResponse) => (
        <VisitorPassStatusSelect
          key={row.id}
          visitorPass={row}
          loading={loadingStates[row.id]}
          updateStatus={handleUpdateStatus}
          organizationSettings={organizationSettings}
        />
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        t(StatusKeyToLabelForExport[visitorPass.status]),
    },
    ...getVisitorLogInviteNameColumns(t, showVisitorInviteName),
    {
      header: t('web.admin.channel.visitor.log.columns.date'),
      key: 'startDate',
      renderCell: (_: any, row: VisitorPassQueryResponse) => (
        <span>{dateFormatter(row.startDatetime, SIMPLE_DATE, timeZone)}</span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.startDatetime,
    },
    {
      header: t('web.admin.channel.visitor.log.columns.expectedArrival'),
      key: 'startDatetime',
      renderCell: (cell: string) => (
        <span>{dateFormatter(cell, SHORT_TIME_WITH_TZ, timeZone)}</span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.startDatetime,
    },
    {
      header: t('web.admin.channel.visitor.log.columns.expectedDeparture'),
      key: 'endDatetime',
      renderCell: (cell: string, row: VisitorPassQueryResponse) => (
        <span className={styles.expectedDeparture}>
          {dateFormatter(cell, SHORT_TIME_WITH_TZ, timeZone)}
          <IconSet
            recurrenceId={row.recurrenceId}
            watchlistSeverityLevel={
              row.visitorWatchlistMatchInfo?.[0]?.watchlistSeverityLevel
            }
          />
        </span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.endDatetime,
    },
    ...visitorColumns,
    {
      header: t('web.admin.channel.visitor.log.columns.noteToStaff'),
      key: 'staffNote',
      renderCell: (_: string, row: VisitorPassQueryResponse) => (
        <span className={styles.staffNote}>{row?.staffNote}</span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.staffNote,
    },
    {
      header: t('web.admin.channel.visitor.log.columns.host'),
      key: 'hostName',
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.hostName,
    },
    {
      header: t('web.admin.channel.visitor.log.columns.hostCompany'),
      key: 'tenantName',
      renderCell: (cell: string, row: VisitorPassQueryResponse) => (
        <span>{getTenantFloor(cell, row?.floor)}</span>
      ),
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.tenantName,
    },
    {
      header: t('web.admin.channel.visitor.log.columns.submittedBy'),
      key: 'submittedBy',
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.submittedBy,
    },
    {
      header: t('web.admin.channel.visitor.log.columns.registeredFrom'),
      key: 'eventName',
      renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) =>
        visitorPass.eventName,
    },
    {
      header: t('web.admin.channel.visitor.watchlist.title'),
      key: 'watchlistSeverityLevel',
      renderCell: (_: any, visitorPass: VisitorPassQueryResponse) => {
              const matchInfo = visitorPass.visitorWatchlistMatchInfo;

              if (!matchInfo || matchInfo.length === 0) {
                return null;
              }

              const severityLevel =
                matchInfo.length > 1
                  ? getHighestWatchlistSeverityLevel(matchInfo)
                  : matchInfo[0].watchlistSeverityLevel;

              if (!isValidWatchlistSeverityLevel(severityLevel)) {
                return null;
              }

              return (
                <Chip
                  value={getWatchlistSeverityTranslation(severityLevel, t)}
                  size="sm"
                  type={getWatchlistSeverityChipStyle(severityLevel)}
                  testId={`watchlistLevel${severityLevel || 'None'}Chip`}
                />
              );
            },
            renderForCSV: (_: any, visitorPass: VisitorPassQueryResponse) => {
              const matchInfo = visitorPass.visitorWatchlistMatchInfo;

              if (!matchInfo || matchInfo.length === 0) {
                return getWatchlistSeverityTranslation(undefined, t);
              }

              const severityLevel =
                matchInfo.length > 1
                  ? getHighestWatchlistSeverityLevel(matchInfo)
                  : matchInfo[0].watchlistSeverityLevel;

              return getWatchlistSeverityTranslation(severityLevel, t);
            },
          },
          ...customFieldColumns,
  ];

  if (!visitorNotesEnabled) {
    columns = columns.filter(column => column.key !== 'staffNote');
  }

  const rowActions = [
    {
      label: t(
        'web.admin.channel.visitor.log.rowActions.viewVisitorPassDetails'
      ),
      onClick: (row: VisitorPassQueryResponse) => {
        const base62VisitorPassId = convertTo62(row?.id);
        const url = routes.visitorManagementVisitorPassDetails
          .replace(':id', channelSlug || '')
          .replace(':visitorPassId', base62VisitorPassId);

        history.push(url);
      },
    },
    {
      label: t(
        'web.admin.channel.visitor.log.rowActions.viewSubmissionReceipt'
      ),
      onClick: (row: VisitorPassQueryResponse) => {
        const base62SubmissionId = convertTo62(row?.submissionId);
        const url = routes.channelAdminInteraction
          .replace(':id', channelSlug || '')
          .replace(':interactionId', base62SubmissionId);

        history.push(url);
      },
    },
    ...(canPrintVisitorPass
      ? [
          {
            label: t(
              'web.admin.channel.visitor.log.rowActions.printVisitorPass'
            ),
            isDisabled: (row: VisitorPassQueryResponse) =>
              row?.status === EventStatus.EVENT_STATUS_PENDING,
            onClick: (row: VisitorPassQueryResponse) => handlePrintPass(row),
          },
        ]
      : []),
    ...(canDownloadQRCode
      ? [
          {
            label: t('web.admin.channel.visitor.log.rowActions.downloadQRCode'),
            isDisabled: (row: VisitorPassQueryResponse) =>
              row?.status === EventStatus.EVENT_STATUS_PENDING,
            onClick: (row: VisitorPassQueryResponse) =>
              handleDownloadQRCode(row),
          },
        ]
      : []),
  ];

  const onFilterChange = (filters: ColumnFiltersState) => {
    setActiveFilters(filters);
    searchKeyValue = '';

    for (const filter of filters) {
      if (filter.id === 'startDatetime') {
        handleDateFilterChange(filter.value as Date);
      }
    }
  };

  const onColumnVisibilityChange = (
    columnVisibility: ColumnVisibilityState
  ) => {
    setVisibleColumns(columnVisibility);
  };

  const filterVisitorPasses = (
    visitorPasses: VisitorPassQueryResponse[],
    filters: ColumnFiltersState
  ) => {
    return visitorPasses.filter((visitorPass: VisitorPassQueryResponse) => {
      return filters.every(filter => {
        const { id, value } = filter;
        const key = id as keyof VisitorPassQueryResponse;

        if (id === 'watchlistSeverityLevel') {
          const matchInfo = visitorPass.visitorWatchlistMatchInfo;

          if (!matchInfo || matchInfo.length === 0) {
            return false;
          } 
          
          let highestSeverity: WatchlistSeverityLevel;
          
          if (matchInfo.length > 1) {
            highestSeverity = getHighestWatchlistSeverityLevel(matchInfo);
          } else {
            highestSeverity = matchInfo[0].watchlistSeverityLevel;
          }

          const filterValues = Array.isArray(value) ? value : [value];

          return filterValues.includes(highestSeverity);
        }

        if (id in visitorPass) {
          if (Array.isArray(value)) {
            return value.includes(visitorPass[key]);
          }

          if (value instanceof Date) {
            if (!visitorPass.startDatetime) {
              return false;
            }

            const visitorPassDateTime = DateTime.fromISO(
              visitorPass.startDatetime
            ).setZone(timeZone);
            const filterDateTime = DateTime.fromJSDate(value).setZone(timeZone);

            return visitorPassDateTime.hasSame(filterDateTime, 'day');
          }

          return visitorPass[key] === value;
        }

        return false;
      });
    });
  };

  const filterData = (data: VisitorPassRow[], searchKey: string) => {
    return data.filter(row => {
      return keywordFilter(row, searchKey);
    });
  };

  const exportCurrentPageAsCSV = () => {
    let filteredDataWithoutsearch: VisitorPassQueryResponse[] = [];

    if (
      data &&
      data.scheduledVisits &&
      data.scheduledVisits.result &&
      data.scheduledVisits.result.visits
    ) {
      const watchlistFilteredVisits = filterVisitorPassesByWatchlistV2(
        data.scheduledVisits.result.visits
      );

      filteredDataWithoutsearch = filterVisitorPasses(
        watchlistFilteredVisits,
        activeFilters
      );
    }

    const filteredData = filterData(filteredDataWithoutsearch, searchKeyValue);

    const filteredColumn = visibleColumns
      ? columns.filter(
          item =>
            visibleColumns[item.key] !== false || mandatoryKeys.has(item.key)
        )
      : columns;

    exportCSV(
      filteredData,
      filteredColumn,
      `scheduled-visitor-current-page-logs-${Date.now()}.csv`,
      channel
    );
  };

  const exportAllAsCSV = async () => {
    try {
      const scheduledVisitsData = await getScheduledVisits(
        startDate,
        oneYearFromNow,
        channel._id,
        scheduledVisitsQuery
      );

      exportCSV(
        filterVisitorPassesByWatchlistV2(scheduledVisitsData),
        columns,
        `scheduled-visitor-all-logs-${Date.now()}.csv`,
        channel
      );
    } catch (err) {
      console.error(err);
      window.Toast.show(err.message, 5000);
    }
  };

  const exportOptions = [
    {
      label: t('web.admin.visitorManagement.exportSelection'),
      onClick: exportCurrentPageAsCSV,
    },
    {
      label: t('web.admin.visitorManagement.exportAll'),
      onClick: exportAllAsCSV,
    },
  ];

  const getAllValidStatuses = () => {
    if (selectedVisitorPasses.length === 0) return [];

    const allValidTransitions = selectedVisitorPasses
      .map(pass => {
        // Get allowed statuses considering visitor validation rules
        const allowedStatuses = getAllowedStatuses(pass, organizationSettings);

        if (allowedStatuses && allowedStatuses.length > 0) {
          return allowedStatuses;
        }

        return (pass.validStateTransitions || []).filter(
          status =>
            status &&
            status.trim() !== '' &&
            !INVALID_TRANSITION_STATUSES.has(status)
        );
      })
      .filter(transitions => transitions.length > 0);

    if (allValidTransitions.length === 0) return [];

    let intersectionStatuses = allValidTransitions[0].filter(status =>
      allValidTransitions.every(transitions => transitions.includes(status))
    );

    // If all selected visits are in the same status, exclude that status from the dropdown
    const allSameStatus = selectedVisitorPasses.every(
      pass => pass.status === selectedVisitorPasses[0].status
    );

    if (allSameStatus && selectedVisitorPasses.length > 0) {
      const currentStatus = selectedVisitorPasses[0].status;

      intersectionStatuses = intersectionStatuses.filter(
        status => status !== currentStatus
      );
    }

    if (!displayAllBulkActionStatuses) {
      intersectionStatuses = intersectionStatuses.filter(
        status => !unavailableForBulkActionStatuses.includes(status)
      );
    }

    return intersectionStatuses;
  };

  const renderBulkStatusUpdateAction = () => {
    const allValidStatuses = getAllValidStatuses();

    const hasValidStatuses = allValidStatuses.length > 0;

    const statusMenuItems = allValidStatuses
      .filter(status => StatusKeyToLabelForStatusUpdate[status])
      .map(status => ({
        label: t(StatusKeyToLabelForStatusUpdate[status]),
        onSelect: () => handleBulkStatusUpdate(status),
        testId: `bulk-status-option-${status}`,
      }));

    return (
      <PopupButton
        variant="secondary"
        size="large"
        items={statusMenuItems}
        disabled={!hasValidStatuses}
        testId="bulk-status-update-button"
        menuTestId="bulk-status-menu"
      >
        {t('web.admin.channel.visitor.log.bulkActions.updateStatus')}
      </PopupButton>
    );
  };

  const renderClearSelectionAction = () => {
    return (
      <Button
        onClick={handleClearSelection}
        variant="text"
        size="large"
        className={styles.clearSelectionButton}
      >
        {t('web.admin.channel.visitor.log.bulkActions.clearSelection')}
      </Button>
    );
  };

  const renderSelectedCount = () => {
    const currentPageData = data?.scheduledVisits?.result?.visits || [];
    const totalRows =
      data?.scheduledVisits?.totalCount || currentPageData.length;
    const isAllSelected = selectedRows.length === currentPageData.length;
    const hasMultiplePages = totalRows > currentPageData.length;

    let messageKey = 'web.admin.channel.visitor.log.bulkActions.selectedCount';
    const count = selectedRows.length;

    if (isAllSelected && !hasMultiplePages) {
      messageKey =
        'web.admin.channel.visitor.log.bulkActions.selectedCountOnePage';
    }

    if (isAllSelected && hasMultiplePages) {
      messageKey =
        'web.admin.channel.visitor.log.bulkActions.selectedCountAllPages';
    }

    return (
      <M className={styles.selectedCountText}>
        <Trans i18nKey={messageKey} values={{ count }} />
      </M>
    );
  };

  const tableBulkActions = bulkActionsEnabled
    ? [
        {
          renderAction: renderBulkStatusUpdateAction,
        },
        {
          renderAction: renderClearSelectionAction,
        },
        {
          renderAction: renderSelectedCount,
        },
      ]
    : [];

  return (
    <>
      <div className={styles.scheduledVisitorTableContainer}>
        <Table
          isLoading={loading}
          columns={columns}
          data={filterVisitorPassesByWatchlistV2(data?.scheduledVisits?.result?.visits || [])}
          pagination="client"
          hasKeywordFilter
          customKeywordFilterFn={keywordFilter}
          onColumnVisibilityChange={onColumnVisibilityChange}
          rowActions={rowActions}
          onFilterChange={onFilterChange}
          onRefreshClick={refresh}
          filters={VisitorLogTableFilters({
            dateFilter: NativeFilterTypes.Date,
            statusListItems,
            hostName: data?.scheduledVisits?.result?.tenantNames || [],
            registeredFrom: data?.scheduledVisits?.result?.eventNames || [],
            minDate: currentTime.toJSDate(),
            maxDate: oneYearFromNow,
            timeZone,
            t,
          })}
          activeFilters={activeFilters}
          exportOptions={exportOptions}
          showColumnVisibility
          isCustomFieldEnabled={customFieldsEnabled}
          columnVisibility={effectiveColumnVisibility}
          customControls={
            customFieldColumns.length > 0 ? (
              <Alert type={AlertType.info} className={styles.customFieldsInfoBanner}>
                {t('web.admin.channel.visitor.log.customFields.infoBanner', {
                  count: customFieldColumns.length,
                })}
              </Alert>
            ) : undefined
          }
          {...(bulkActionsEnabled && {
            bulkActions: tableBulkActions,
            rowSelection: selectedRows,
            onSelectionChange: handleMultiselectChange,
            isSelectable: bulkActionsEnabled,
            key: tableKey,
          })}
        />
      </div>

      {isSelectPrintersModalOpen && selectedVisitorPass && (
        <PrinterSelectionModal
          isOpen={isSelectPrintersModalOpen}
          onClose={() => {
            setIsSelectPrintersModalOpen(false);
            setSelectedVisitorPass(null);
          }}
          connectedPrinters={connectedPrinters}
          visitorPass={selectedVisitorPass}
          printPass={printPass}
        />
      )}
    </>
  );
}

function IconSet({
  recurrenceId,
  watchlistSeverityLevel: _watchlistSeverityLevel,
}: {
  recurrenceId: string | undefined;
  watchlistSeverityLevel: WatchlistSeverityLevel | undefined;
}) {
  const { t } = useTranslation();
  const theme = useThemeColors();

  return (
    <span className={styles.iconSet}>
      {recurrenceId ? (
        <Tooltip
          placement="bottom"
          TooltipComponent={t(TRANSLATION_KEYS.recurrenceTooltip)}
        >
          <Icon
            name="repeat"
            set={ICON_SET_FONTAWESOME}
            type="far"
            style={{ color: theme.secondary, cursor: 'pointer' }}
          />
        </Tooltip>
      ) : (
        <Icon name="repeat" set={ICON_SET_FONTAWESOME} type="far" />
      )}
      <Icon name="file-alt" set={ICON_SET_FONTAWESOME} type="far" />
    </span>
  );
}

export function keywordFilter(row: VisitorPassRow, keyword: string): boolean {
  const searchableString = [
    row.visitor.firstName,
    row.visitor.lastName,
    row.visitor.company,
    row.visitor.email,
    row.companyName,
    row.submittedBy,
    row.eventName,
    row.hostName,
    row.tenantName,
    row.floor,
    row.staffNote,
    row.inviteName,
  ]
    .map(field => field?.trim() || '')
    .join(' ')
    .toLowerCase();

  searchKeyValue = keyword.trim();

  return searchableString.includes(searchKeyValue.toLowerCase());
}
